import {
  ArrowBack as BackIcon,
  AutoAwesome as AgentIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import type { AccountStreamMatch, Stream } from "../../types/accountIntelligence";
import { getMatchesForStream, getStreamById } from "./mockData";
import AccountStateChip from "./shared/AccountStateChip";
import OpportunityAngleCard from "./shared/OpportunityAngleCard";
import { SIGNAL_TYPE_LABEL } from "./shared/OpportunityAngleCard";

// ─── Types ─────────────────────────────────────────────────────────────────────

type StreamTab = "overview" | "accounts" | "opportunities" | "signals" | "settings";

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

const STATUS_BADGE_CONFIG: Array<{ key: keyof Stream["statusCounts"]; label: string; color: string }> = [
  { key: "engaged", label: "Engaged", color: "#2e7d32" },
  { key: "watching", label: "Watching", color: "#1976d2" },
  { key: "candidate", label: "Candidates", color: "#9e9e9e" },
  { key: "deprioritized", label: "Deprioritized", color: "#616161" },
];

function buildAgentDigest(stream: Stream, matches: AccountStreamMatch[]): string {
  const { statusCounts } = stream;
  const engaged = statusCounts.engaged;
  const candidates = statusCounts.candidate;
  const total = stream.accountCount;

  const parts: string[] = [`Monitoring ${total} accounts.`];

  if (engaged > 0) {
    parts.push(`${engaged} account${engaged > 1 ? "s" : ""} currently engaged.`);
  }

  if (candidates > 0) {
    parts.push(`${candidates} new candidate${candidates > 1 ? "s" : ""} awaiting your review.`);
  }

  const topAccount = matches.find(
    (m) => m.signals.length > 0,
  );
  if (topAccount?.topAngle) {
    parts.push(
      `Focus: ${topAccount.account.name} — ${topAccount.topAngle.title}.`,
    );
  }

  return parts.join(" ");
}

const OverviewTab: React.FC<{
  stream: Stream;
  matches: AccountStreamMatch[];
  onOpenAccount: (accountId: string, streamId: string) => void;
}> = ({ stream, matches, onOpenAccount }) => {
  const digest = buildAgentDigest(stream, matches);

  // Focus accounts: those with the most recent signals, capped at 5
  const focusMatches = [...matches]
    .filter((m) => m.signals.length > 0)
    .sort((a, b) => {
      const da = Math.max(...a.signals.map((s) => new Date(s.date).getTime()));
      const db = Math.max(...b.signals.map((s) => new Date(s.date).getTime()));
      return db - da;
    })
    .slice(0, 5);

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Agent digest */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "rgba(25,118,210,0.06)",
          border: "1px solid rgba(25,118,210,0.2)",
          borderRadius: 1.5,
          p: 2.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <AgentIcon sx={{ fontSize: 16, color: "primary.main" }} />
          <Typography variant="caption" fontWeight={700} color="primary.main">
            STREAM DIGEST
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
          {digest}
        </Typography>
      </Paper>

      {/* State count pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {STATUS_BADGE_CONFIG.filter((s) => stream.statusCounts[s.key] > 0).map((s) => (
          <Box
            key={s.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: `${s.color}10`,
              border: `1px solid ${s.color}30`,
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: s.color }} />
            <Typography variant="caption" sx={{ color: s.color, fontWeight: 700 }}>
              {stream.statusCounts[s.key]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Focus accounts */}
      {focusMatches.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
            ACCOUNTS TO FOCUS ON
          </Typography>
          <Stack spacing={1}>
            {focusMatches.map((m) => (
              <Paper
                key={m.id}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.main", backgroundColor: "action.hover" },
                  transition: "border-color 0.15s",
                }}
                onClick={() => onOpenAccount(m.account.id, m.streamId)}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {m.topAngle && (
                    <Typography variant="body2" fontWeight={700} mb={0.25}>
                      {m.topAngle.title}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" color="text.secondary">
                      {m.account.name}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {m.account.geography}
                    </Typography>
                  </Box>
                  {m.signals[0] && (
                    <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
                      {m.signals[0].title}
                    </Typography>
                  )}
                </Box>
                <AccountStateChip state={m.status} />
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

// ─── Accounts Tab ─────────────────────────────────────────────────────────────

const AccountsTab: React.FC<{
  matches: AccountStreamMatch[];
  onOpenAccount: (accountId: string, streamId: string) => void;
}> = ({ matches, onOpenAccount }) => (
  <Box sx={{ p: 0 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
          {["Account", "Status", "Fit", "Top Fit Reason", "Latest Signal", "Angles"].map(
            (h) => (
              <TableCell
                key={h}
                sx={{ fontWeight: 700, fontSize: "0.72rem", color: "text.secondary" }}
              >
                {h}
              </TableCell>
            ),
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {matches.map((m) => (
          <TableRow
            key={m.id}
            hover
            sx={{ cursor: "pointer" }}
            onClick={() => onOpenAccount(m.account.id, m.streamId)}
          >
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {m.account.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {m.account.geography}
              </Typography>
            </TableCell>
            <TableCell>
              <AccountStateChip state={m.status} />
            </TableCell>
            <TableCell>
              <Chip
                label={m.fitScore}
                size="small"
                sx={{ fontSize: "0.68rem", textTransform: "capitalize",
                  color: m.fitScore === "high" ? "#2e7d32" : m.fitScore === "medium" ? "#e65100" : "#616161",
                  backgroundColor: m.fitScore === "high" ? "rgba(46,125,50,0.1)" : m.fitScore === "medium" ? "rgba(230,81,0,0.1)" : "rgba(97,97,97,0.1)",
                }}
              />
            </TableCell>
            <TableCell>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, display: "block" }}>
                {m.fitRationale.slice(0, 80)}…
              </Typography>
            </TableCell>
            <TableCell>
              {m.signals[0] ? (
                <Chip
                  label={m.signals[0].title.slice(0, 30)}
                  size="small"
                  sx={{ fontSize: "0.68rem", maxWidth: 180 }}
                />
              ) : (
                <Typography variant="caption" color="text.disabled">
                  None
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Typography variant="body2" fontWeight={600} textAlign="center">
                {m.angles.length}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

// ─── Opportunities Tab ────────────────────────────────────────────────────────

const OpportunitiesTab: React.FC<{ matches: AccountStreamMatch[] }> = ({ matches }) => {
  const allAngles = matches.flatMap((m) =>
    m.angles.map((a) => ({ angle: a, account: m.account })),
  );

  if (allAngles.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">No opportunity angles identified yet.</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
        {allAngles.length} OPPORTUNITY ANGLES ACROSS {matches.filter((m) => m.angles.length > 0).length} ACCOUNTS
      </Typography>
      {allAngles.map(({ angle, account }) => (
        <Box key={angle.id}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} mb={0.5} display="block">
            {account.name}
          </Typography>
          <OpportunityAngleCard angle={angle} defaultExpanded={false} />
        </Box>
      ))}
    </Stack>
  );
};

// ─── Signals Tab ──────────────────────────────────────────────────────────────

const SignalsTab: React.FC<{ matches: AccountStreamMatch[] }> = ({ matches }) => {
  const allSignals = matches
    .flatMap((m) => m.signals.map((s) => ({ signal: s, accountName: m.account.name })))
    .sort((a, b) => new Date(b.signal.date).getTime() - new Date(a.signal.date).getTime());

  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
          {["Signal", "Account", "Type", "Date", "Relevance", "Confidence"].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.72rem", color: "text.secondary" }}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {allSignals.map(({ signal, accountName }) => (
          <TableRow key={signal.id} hover>
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {signal.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {signal.description.slice(0, 60)}…
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{accountName}</Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={SIGNAL_TYPE_LABEL[signal.type] ?? signal.type}
                size="small"
                sx={{ fontSize: "0.68rem" }}
              />
            </TableCell>
            <TableCell>
              <Typography variant="caption">{formatDate(signal.date)}</Typography>
            </TableCell>
            <TableCell>
              <LevelDot level={signal.relevance} />
            </TableCell>
            <TableCell>
              <LevelDot level={signal.confidence} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// ─── Settings Tab ─────────────────────────────────────────────────────────────

const SettingsTab: React.FC<{ stream: Stream }> = ({ stream }) => (
  <Stack spacing={3} sx={{ p: 3, maxWidth: 600 }}>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
        PRODUCT / SERVICE
      </Typography>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          {stream.product.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {stream.product.description}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {stream.product.capabilityTags.map((t) => (
            <Chip key={t} label={t} size="small" sx={{ fontSize: "0.68rem" }} />
          ))}
        </Box>
      </Paper>
    </Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
        ICP DEFINITION
      </Typography>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          {stream.icp.name}
        </Typography>
        <Stack spacing={1}>
          <FieldRow label="Account types" value={stream.icp.accountTypes.join(", ")} />
          <FieldRow label="Industries" value={stream.icp.industries.join(", ")} />
          <FieldRow label="Geography" value={stream.icp.geography.join(", ")} />
          <FieldRow label="Company size" value={stream.icp.companySize} />
          <FieldRow label="Exclusions" value={stream.icp.exclusions} />
        </Stack>
      </Paper>
    </Box>
  </Stack>
);

const FieldRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

// ─── Level dot helper ─────────────────────────────────────────────────────────

const LEVEL_COLOR: Record<string, string> = {
  high: "#2e7d32",
  medium: "#e65100",
  low: "#616161",
};

const LevelDot: React.FC<{ level: string }> = ({ level }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <CircleIcon sx={{ fontSize: 8, color: LEVEL_COLOR[level] }} />
    <Typography variant="caption" sx={{ textTransform: "capitalize" }}>
      {level}
    </Typography>
  </Box>
);

// ─── Main StreamDetail ─────────────────────────────────────────────────────────

interface StreamDetailProps {
  streamId: string;
  onBack: () => void;
  onOpenAccount: (accountId: string, streamId: string) => void;
}

const StreamDetail: React.FC<StreamDetailProps> = ({ streamId, onBack, onOpenAccount }) => {
  const [activeTab, setActiveTab] = useState<StreamTab>("overview");

  const stream = getStreamById(streamId);
  const matches = getMatchesForStream(streamId);

  if (!stream) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Stream not found.</Typography>
      </Box>
    );
  }

  const confidenceColor =
    stream.agentConfidence === "high"
      ? "#2e7d32"
      : stream.agentConfidence === "medium"
      ? "#e65100"
      : "#616161";

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Feature header */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Tooltip title="Back to Streams">
          <IconButton size="small" onClick={onBack} sx={{ color: "text.secondary" }}>
            <BackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {stream.product.name}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {stream.icp.name}
          </Typography>
        </Box>
        <Chip
          size="small"
          icon={<AgentIcon sx={{ fontSize: 12 }} />}
          label={`${stream.agentConfidence} confidence`}
          sx={{
            color: confidenceColor,
            backgroundColor: `${confidenceColor}15`,
            border: `1px solid ${confidenceColor}40`,
            fontSize: "0.7rem",
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        />
        <Typography variant="caption" color="text.secondary" flexShrink={0}>
          {stream.accountCount} accounts
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ px: 2, minHeight: 40 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          {(
            [
              ["overview", "Overview"],
              ["accounts", "Accounts"],
              ["opportunities", "Opportunities"],
              ["signals", "Signals"],
              ["settings", "Definition"],
            ] as [StreamTab, string][]
          ).map(([val, label]) => (
            <Tab
              key={val}
              value={val}
              label={label}
              sx={{ textTransform: "none", fontSize: "0.85rem", minHeight: 40, py: 0 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {activeTab === "overview" && <OverviewTab stream={stream} matches={matches} onOpenAccount={onOpenAccount} />}
        {activeTab === "accounts" && (
          <AccountsTab matches={matches} onOpenAccount={onOpenAccount} />
        )}
        {activeTab === "opportunities" && <OpportunitiesTab matches={matches} />}
        {activeTab === "signals" && <SignalsTab matches={matches} />}
        {activeTab === "settings" && <SettingsTab stream={stream} />}
      </Box>
    </Box>
  );
};

export default StreamDetail;
